import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '@/contexts/LanguageContext';

const PILLARS_SL = [
  {
    key: 'green',
    icon: '🔋',
    title: 'ZELENA MOBILNOST',
    color: '#4CAF82',
    points: [
      { label: 'Hibridni vozni park', desc: 'Peugeot 2008 Hybrid za vse terenske ekipe.' },
      { label: 'Manj emisij',         desc: 'Nižji CO₂ odtis pri 15 obiskih/dan.' },
    ],
  },
  {
    key: 'local',
    icon: '📍',
    title: 'LOKALNA OPTIMIZACIJA',
    color: '#4CAF82',
    points: [
      { label: 'Regijske ekipe',       desc: 'Center-Zahod in Vzhod — manj prevoženih kilometrov.' },
      { label: 'Strateška lokacija',   desc: 'HQ in skladišče Ljubljana (Letališka 34).' },
    ],
  },
  {
    key: 'digital',
    icon: '📱',
    title: 'DIGITAL-FIRST & RSP',
    color: '#4CAF82',
    points: [
      { label: 'Brezpapirno',    desc: '100 % poročanje in dokumentacija prek iPadov.' },
      { label: 'Socialna trajnost', desc: 'Polna skladnost z RSP načeli PMI.' },
    ],
  },
];

const PILLARS_EN = [
  {
    key: 'green',
    icon: '🔋',
    title: 'GREEN MOBILITY',
    color: '#4CAF82',
    points: [
      { label: 'Hybrid fleet',   desc: 'Peugeot 2008 Hybrid for all field teams.' },
      { label: 'Lower emissions', desc: 'Reduced CO₂ footprint at 15 visits/day.' },
    ],
  },
  {
    key: 'local',
    icon: '📍',
    title: 'LOCAL OPTIMISATION',
    color: '#4CAF82',
    points: [
      { label: 'Regional teams',      desc: 'Centre-West and East — fewer kilometres driven.' },
      { label: 'Strategic location',  desc: 'HQ and warehouse Ljubljana (Letališka 34).' },
    ],
  },
  {
    key: 'digital',
    icon: '📱',
    title: 'DIGITAL-FIRST & RSP',
    color: '#4CAF82',
    points: [
      { label: 'Paperless',        desc: '100% reporting and documentation via iPads.' },
      { label: 'Social sustainability', desc: 'Full compliance with PMI RSP principles.' },
    ],
  },
];

export const SustainabilitySection = () => {
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const PILLARS = lang === 'sl' ? PILLARS_SL : PILLARS_EN;

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      gsap.fromTo('.sus-header',
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, duration: 0.7, scrollTrigger: { trigger: sectionRef.current, start: 'top 70%', once: true } }
      );
      gsap.fromTo('.sus-card',
        { opacity: 0, y: 36 },
        {
          opacity: 1, y: 0, duration: 0.65, ease: 'power3.out', stagger: 0.12,
          scrollTrigger: { trigger: '.sus-grid', start: 'top 78%', once: true },
        }
      );
      gsap.fromTo('.sus-tagline',
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: 0.6, scrollTrigger: { trigger: '.sus-tagline', start: 'top 90%', once: true } }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  const GREEN = '#4CAF82';

  return (
    <section
      ref={sectionRef}
      id="sustainability"
      className="scroll-section min-h-screen flex items-center px-4 py-20"
    >
      <div className="section-content max-w-[1100px] mx-auto w-full">

        {/* Header */}
        <div className="sus-header text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-[#4CAF82]/30 bg-[#4CAF82]/8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4CAF82]" />
            <span className="font-mono text-xs text-[#4CAF82] uppercase tracking-widest">
              {lang === 'sl' ? 'Trajnostni pristop' : 'Sustainable Approach'}
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            {lang === 'sl'
              ? <>Trajnostna <span style={{ color: GREEN }}>Operativa</span></>
              : <>Sustainable <span style={{ color: GREEN }}>Operations</span></>
            }
          </h2>
          <p className="text-white/40 max-w-xl mx-auto text-base leading-relaxed">
            {lang === 'sl'
              ? 'Zelena mobilnost, lokalna optimizacija in digitalni procesi za odgovorno rast.'
              : 'Green mobility, local optimisation and digital processes for responsible growth.'}
          </p>
        </div>

        {/* 3-column grid */}
        <div className="sus-grid grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {PILLARS.map((pillar) => (
            <div
              key={pillar.key}
              className="sus-card group relative rounded-2xl border border-white/8 bg-white/3 backdrop-blur-sm p-6 flex flex-col gap-5 hover:border-white/15 hover:bg-white/5 transition-all duration-300"
            >
              {/* Top accent line */}
              <div
                className="absolute top-0 left-6 right-6 h-px rounded-full"
                style={{ background: `linear-gradient(90deg, ${GREEN}70, transparent)` }}
              />

              {/* Hover glow */}
              <div
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: `radial-gradient(ellipse 80% 50% at 50% 0%, ${GREEN}10 0%, transparent 70%)` }}
              />

              {/* Title */}
              <div className="relative flex items-center gap-3">
                <h3
                  className="font-mono text-sm font-bold tracking-widest uppercase"
                  style={{ color: GREEN }}
                >
                  {pillar.title}
                </h3>
              </div>

              {/* Points */}
              <div className="relative flex flex-col gap-4">
                {pillar.points.map((pt, i) => (
                  <div key={i} className="flex gap-3">
                    <span
                      className="w-1 h-1 rounded-full flex-shrink-0 mt-2"
                      style={{ background: GREEN }}
                    />
                    <div>
                      <span className="text-white/80 text-sm font-semibold">{pt.label}: </span>
                      <span className="text-white/45 text-sm leading-relaxed">{pt.desc}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom hover line */}
              <div
                className="absolute bottom-0 left-1/2 -translate-x-1/2 h-px w-0 group-hover:w-2/3 transition-all duration-500 rounded-full"
                style={{ background: GREEN }}
              />
            </div>
          ))}
        </div>

        {/* Tagline */}
        <div className="sus-tagline relative rounded-2xl border border-[#4CAF82]/25 bg-[#4CAF82]/5 backdrop-blur-sm px-8 py-5 text-center overflow-hidden">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: `radial-gradient(ellipse 60% 80% at 50% 50%, ${GREEN}07 0%, transparent 70%)` }}
          />
          <p className="relative font-display text-base sm:text-lg font-semibold text-white/80 leading-relaxed">
            <span style={{ color: GREEN }} className="font-bold">Wibe Solutions: </span>
            {lang === 'sl'
              ? 'Učinkovitost z minimalnim vplivom na okolje.'
              : 'Efficiency with minimal environmental impact.'}
          </p>
        </div>

      </div>
    </section>
  );
};
